import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { reportCardsApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'

const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(date)
}

const sanitizeFileName = (value) => (
  String(value || 'document')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
)

const getStatusLabel = (status) => {
  if (status === 'published') return 'Published'
  if (status === 'absent') return 'Absent'
  if (status === 'withheld') return 'Withheld'
  if (status === 'pending') return 'Pending'
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft'
}

const getStatusClasses = (status) => {
  if (status === 'published') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (status === 'absent' || status === 'withheld') return 'bg-amber-50 text-amber-700 border-amber-200'
  if (status === 'pending') return 'bg-slate-100 text-slate-600 border-slate-200'
  return 'bg-slate-100 text-slate-600 border-slate-200'
}

const getExamId = (exam) => exam?.examId || exam?._id || exam?.id

function StatCard({ label, value, tone = 'bg-slate-50 text-slate-900' }) {
  return (
    <div className="card p-4">
      <div className={`inline-flex rounded-2xl px-3 py-1 text-xs font-semibold ${tone}`}>
        {label}
      </div>
      <p className="mt-4 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  )
}

export default function ReportCardDetailPage() {
  const { studentId, sessionId } = useParams()
  const [reportCard, setReportCard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [expandedExams, setExpandedExams] = useState(false)

  useEffect(() => {
    const fetchReportCard = async () => {
      if (!studentId || !sessionId) {
        setReportCard(null)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const data = await reportCardsApi.getCard(studentId, sessionId)
        setReportCard(data)
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load report card')
        setReportCard(null)
      } finally {
        setLoading(false)
      }
    }

    fetchReportCard()
  }, [sessionId, studentId])

  const summary = reportCard?.summary || {}
  const student = reportCard?.student || {}
  const session = reportCard?.session || {}
  const exams = Array.isArray(reportCard?.exams) ? reportCard.exams : []
  const subjectSummary = Array.isArray(reportCard?.subjectSummary) ? reportCard.subjectSummary : []

  const studentName = useMemo(() => (
    `${student?.firstName || ''} ${student?.lastName || ''}`.trim() || student?.name || 'Student'
  ), [student?.firstName, student?.lastName, student?.name])

  const backLink = studentId ? `/report-cards?studentId=${encodeURIComponent(studentId)}` : '/report-cards'
  const sessionDateRange = [session?.formattedStartDate || formatDate(session?.startDate), session?.formattedEndDate || formatDate(session?.endDate)]
    .filter((value) => value && value !== '—')
    .join(' - ')

  const handleDownload = async () => {
    if (!studentId || !sessionId) return

    try {
      setDownloadLoading(true)
      const response = await reportCardsApi.downloadCard(studentId, sessionId)
      const objectUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `${sanitizeFileName(`report-card-${student?.admissionNo || studentId}-${session?.name || sessionId}`)}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
      toast.success('Report card downloaded')
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download report card')
    } finally {
      setDownloadLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
      </div>
    )
  }

  if (!reportCard) {
    return (
      <div className="card p-8 text-center">
        <FileText className="mx-auto h-10 w-10 text-gray-200" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Report card not available</h2>
        <p className="mt-2 text-sm text-gray-500">This session may not be published yet, or the record could not be loaded.</p>
        <Link to={backLink} className="btn-secondary mt-5 inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Report Cards
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={session?.name || 'Report Card'}
        subtitle={`Detailed session performance for ${studentName}.`}
        chips={[
          student?.className || student?.class || 'Class not assigned',
          student?.admissionNo ? `Adm. ${student.admissionNo}` : 'Admission no not set',
          session?.academicYear || session?.year || 'Academic year not set',
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={handleDownload} className="btn-primary flex items-center gap-2" disabled={downloadLoading}>
              {downloadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Report Card
            </button>
            <Link to={backLink} className="btn-secondary inline-flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
          </div>
        }
      />

      <div className="overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-900 to-primary-900 p-6 text-white shadow-sm">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/85">
              <BookOpen className="h-3.5 w-3.5" /> Session overview
            </div>
            <h3 className="mt-4 text-3xl font-bold tracking-tight">{studentName}</h3>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-white/75">
              <span className="rounded-full bg-white/10 px-3 py-1.5">{student?.className || student?.class || 'Class not assigned'}</span>
              {student?.admissionNo && <span className="rounded-full bg-white/10 px-3 py-1.5">Admission {student.admissionNo}</span>}
              {sessionDateRange && <span className="rounded-full bg-white/10 px-3 py-1.5">{sessionDateRange}</span>}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/10 p-5 text-center backdrop-blur-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">Overall</p>
            <p className="mt-3 text-4xl font-black">{Math.round(summary?.overallPercentage || 0)}%</p>
            <p className="mt-2 text-sm font-semibold text-white/80">Grade {summary?.overallGrade || '--'}</p>
          </div>
        </div>
      </div>

      {summary?.pendingCount > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold">Some results are still pending</p>
              <p className="mt-1 text-sm">
                {summary.pendingCount} exam result{summary.pendingCount === 1 ? '' : 's'} are not published yet, so totals may change.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Passed" value={summary?.passCount ?? '--'} tone="bg-emerald-50 text-emerald-700" />
        <StatCard label="Failed" value={summary?.failCount ?? '--'} tone="bg-red-50 text-red-700" />
        <StatCard label="Absent" value={summary?.absentCount ?? 0} tone="bg-amber-50 text-amber-700" />
        <StatCard
          label="Marks"
          value={`${summary?.obtainedMarks ?? '--'} / ${summary?.totalMarks ?? '--'}`}
          tone="bg-blue-50 text-blue-700"
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(280px,0.9fr)]">
        <div className="card overflow-hidden">
          <div className="border-b border-gray-100 px-5 py-4">
            <h3 className="text-lg font-bold text-gray-900">Subject Performance</h3>
            <p className="mt-1 text-sm text-gray-500">Per-subject performance for this published session.</p>
          </div>
          {subjectSummary.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-400">No subject summary is available yet.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {subjectSummary.map((item, index) => {
                const percentage = Math.round(item?.percentage || 0)
                const gradeTone = percentage >= 75
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : percentage >= 50
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-red-50 text-red-700 border-red-200'

                return (
                  <div key={item?.subject || index} className="px-5 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{item?.subject || `Subject ${index + 1}`}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {item?.obtainedMarks ?? '--'} / {item?.totalMarks ?? '--'} marks
                          {typeof item?.examsCount === 'number' ? ` · ${item.examsCount} exam${item.examsCount === 1 ? '' : 's'}` : ''}
                        </p>
                      </div>
                      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${gradeTone}`}>
                        {percentage}%
                      </span>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={`h-2 rounded-full ${percentage >= 75 ? 'bg-emerald-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="card p-5">
          <h3 className="text-lg font-bold text-gray-900">Session Snapshot</h3>
          <div className="mt-5 space-y-4">
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Academic Year</p>
              <p className="mt-2 text-sm font-medium text-gray-900">{session?.academicYear || session?.year || 'Not set'}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Session Range</p>
              <p className="mt-2 text-sm font-medium text-gray-900">{sessionDateRange || 'Not available'}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Summary</p>
              <div className="mt-2 space-y-2 text-sm text-gray-600">
                <p>{subjectSummary.length} subjects in the session summary.</p>
                <p>{exams.length} exam{exams.length === 1 ? '' : 's'} in the breakdown.</p>
                <p>{summary?.overallGrade ? `Overall grade ${summary.overallGrade}.` : 'Overall grade is not available yet.'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <button
          type="button"
          onClick={() => setExpandedExams((current) => !current)}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <h3 className="text-lg font-bold text-gray-900">Exam Breakdown</h3>
            <p className="mt-1 text-sm text-gray-500">Open the same per-exam result details admins can inspect in the RN app.</p>
          </div>
          <div className="flex items-center gap-3 text-sm font-medium text-gray-500">
            <span>{exams.length} exam{exams.length === 1 ? '' : 's'}</span>
            {expandedExams ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </button>

        {expandedExams && (
          exams.length === 0 ? (
            <div className="border-t border-gray-100 px-5 py-10 text-center text-sm text-gray-400">No exam-wise records are available.</div>
          ) : (
            <div className="border-t border-gray-100 p-5">
              <div className="grid gap-4 xl:grid-cols-2">
                {exams.map((exam, index) => {
                  const examId = getExamId(exam)
                  return (
                    <div key={examId || index} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900">{exam?.title || `Exam ${index + 1}`}</p>
                          <p className="mt-1 text-sm text-gray-500">
                            {exam?.subject || 'Subject not set'} · {exam?.formattedDate || formatDate(exam?.date)}
                          </p>
                        </div>
                        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${getStatusClasses(exam?.resultStatus)}`}>
                          {exam?.grade || '--'}
                        </span>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 shadow-sm">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          {exam?.marksObtained ?? '--'} / {exam?.maxMarks ?? '--'}
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 shadow-sm">
                          <Clock3 className="h-4 w-4 text-amber-600" />
                          Pass mark {exam?.passMarks ?? '--'}
                        </span>
                        <span className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold ${getStatusClasses(exam?.resultStatus)}`}>
                          {getStatusLabel(exam?.resultStatus)}
                        </span>
                      </div>

                      {exam?.feedback && (
                        <p className="mt-4 rounded-2xl bg-white px-4 py-3 text-sm text-gray-600 shadow-sm">
                          {exam.feedback}
                        </p>
                      )}

                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-gray-500">
                          {Math.round(exam?.percentage || 0)}% · {exam?.isPassed ? 'Pass' : 'Fail'}
                        </div>
                        {examId ? (
                          <Link to={`/marksheets/${studentId}/${examId}`} className="btn-secondary inline-flex items-center gap-2 text-sm">
                            <FileText className="h-4 w-4" /> View Marksheet
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}
